import { useCallback, useEffect, useState } from 'react'
import { auth } from '@/core/firebase/client'

/**
 * One-time welcome dialog for redeemed preview tokens. Keyed by
 * `previewTokenId` (custom claim) so a freshly-minted token shows the
 * explainer once, and follow-up sessions on the same token don't.
 *
 * Per-device by design: a prospect opening the link on a second device is
 * a new context worth re-introducing, and we don't have a server-side
 * "seen" flag (and don't need one — losing one cookie shouldn't lose the
 * explainer either).
 */
const STORAGE_PREFIX = 'cm.preview-welcome.'

interface PreviewWelcomeState {
  /** True once the auth token has been read and we know whether to show. */
  resolved: boolean
  shouldShow: boolean
}

const INITIAL: PreviewWelcomeState = { resolved: false, shouldShow: false }

function storageKey(tokenId: string): string {
  return `${STORAGE_PREFIX}${tokenId}`
}

function hasSeen(tokenId: string): boolean {
  try {
    return localStorage.getItem(storageKey(tokenId)) === '1'
  } catch {
    return false
  }
}

function markSeen(tokenId: string): void {
  try {
    localStorage.setItem(storageKey(tokenId), '1')
  } catch {
    /* private mode / quota — silent */
  }
}

export interface PreviewWelcomeApi extends PreviewWelcomeState {
  dismiss: () => void
}

export function usePreviewWelcome(): PreviewWelcomeApi {
  const [tokenId, setTokenId] = useState<string | null>(null)
  const [state, setState] = useState<PreviewWelcomeState>(INITIAL)

  useEffect(() => {
    let cancelled = false
    const unsub = auth.onIdTokenChanged(async (user) => {
      if (cancelled) return
      if (!user) {
        setTokenId(null)
        setState({ resolved: true, shouldShow: false })
        return
      }
      try {
        const result = await user.getIdTokenResult()
        const claims = result.claims as { previewTokenId?: string }
        const id = typeof claims.previewTokenId === 'string' ? claims.previewTokenId : null
        if (cancelled) return
        setTokenId(id)
        setState({
          resolved: true,
          shouldShow: id != null && !hasSeen(id),
        })
      } catch {
        if (cancelled) return
        setState({ resolved: true, shouldShow: false })
      }
    })
    return () => {
      cancelled = true
      unsub()
    }
  }, [])

  const dismiss = useCallback(() => {
    if (tokenId) markSeen(tokenId)
    setState((prev) => ({ ...prev, shouldShow: false }))
  }, [tokenId])

  return { ...state, dismiss }
}
